import React, { useState, useEffect, useCallback } from 'react';
import { Plus, CheckCircle, Loader, Pencil, X, Search } from 'lucide-react';
import API from '../api';

const LabelInput = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 11, color: '#718096', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    {children}
  </div>
);

const TIPI = [
  { id: 'ordinario',          label: 'Tribunale ordinario', colore: '#2B6CB0' },
  { id: 'corte_appello',      label: "Corte d'Appello",     colore: '#8E44AD' },
  { id: 'tar',                label: 'TAR',                 colore: '#16A085' },
  { id: 'consiglio_di_stato', label: 'Consiglio di Stato',  colore: '#C05621' },
];
const tipoInfo = (id) => TIPI.find(t => t.id === id) || { id, label: id, colore: '#718096' };

const VUOTO = { nome: '', citta: '', sigla_provincia: '', tipo: 'ordinario' };

export default function Tribunali() {
  const [tribunali, setTribunali] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState(null);
  const [avviso, setAvviso] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [ricerca, setRicerca] = useState('');
  const [mostraInattivi, setMostraInattivi] = useState(false);
  const [mostraNuovo, setMostraNuovo] = useState(false);
  const [nuovo, setNuovo] = useState(VUOTO);
  const [salvando, setSalvando] = useState(false);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(VUOTO);

  const carica = useCallback(() => {
    setCaricamento(true);
    API.get('/finanziario/tribunali-tutti')
      .then(r => setTribunali(r.data))
      .catch(() => setErrore('Errore nel caricamento dei tribunali.'))
      .finally(() => setCaricamento(false));
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const normalizza = (d) => ({
    nome: d.nome.trim(),
    citta: d.citta.trim(),
    sigla_provincia: d.sigla_provincia.trim().toUpperCase() || null,
    tipo: d.tipo,
  });

  const valida = (d) => {
    if (!d.nome) return 'Inserisci il nome del tribunale.';
    if (!d.citta) return 'Inserisci la città.';
    if (d.sigla_provincia && d.sigla_provincia.length !== 2) return 'La sigla provincia deve essere di 2 lettere.';
    return null;
  };

  const crea = async () => {
    const d = normalizza(nuovo);
    const err = valida(d);
    if (err) { setErrore(err); return; }
    setSalvando(true); setErrore(null); setAvviso(null);
    try {
      const r = await API.post('/finanziario/tribunali', d);
      setAvviso(`"${r.data.nome}" creato.`);
      setNuovo(VUOTO); setMostraNuovo(false);
      carica();
    } catch (e) {
      setErrore(e.response?.data?.detail || 'Errore nella creazione.');
    } finally {
      setSalvando(false);
    }
  };

  const salvaModifica = async (id) => {
    const d = normalizza(edit);
    const err = valida(d);
    if (err) { setErrore(err); return; }
    setSalvando(true); setErrore(null); setAvviso(null);
    try {
      await API.patch(`/finanziario/tribunali/${id}`, d);
      setAvviso('Tribunale aggiornato.');
      setEditId(null);
      carica();
    } catch (e) {
      setErrore(e.response?.data?.detail || 'Errore nel salvataggio.');
    } finally {
      setSalvando(false);
    }
  };

  const toggleAttivo = async (t) => {
    setErrore(null); setAvviso(null);
    try {
      await API.patch(`/finanziario/tribunali/${t.id}`, { attivo: !t.attivo });
      carica();
    } catch (e) {
      setErrore(e.response?.data?.detail || 'Errore nel cambio stato.');
    }
  };

  const q = ricerca.trim().toLowerCase();
  const visibili = tribunali.filter(t =>
    (mostraInattivi || t.attivo !== false) &&
    (!filtroTipo || t.tipo === filtroTipo) &&
    (!q || t.nome.toLowerCase().includes(q) || (t.citta || '').toLowerCase().includes(q) || (t.sigla_provincia || '').toLowerCase() === q)
  );
  const conteggi = TIPI.reduce((acc, tp) => ({ ...acc, [tp.id]: tribunali.filter(t => t.tipo === tp.id && t.attivo !== false).length }), {});

  const campiForm = (dati, setDati, autoFocus) => (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 1fr', gap: 12, marginBottom: 12 }}>
      <LabelInput label="Nome *">
        <input className="input-filtro" style={{ width: '100%' }} autoFocus={autoFocus}
          value={dati.nome} onChange={e => setDati({ ...dati, nome: e.target.value })}
          placeholder="es. Corte d'Appello di Salerno" />
      </LabelInput>
      <LabelInput label="Città *">
        <input className="input-filtro" style={{ width: '100%' }}
          value={dati.citta} onChange={e => setDati({ ...dati, citta: e.target.value })}
          placeholder="es. Salerno" />
      </LabelInput>
      <LabelInput label="Prov.">
        <input className="input-filtro" style={{ width: '100%', textTransform: 'uppercase' }} maxLength={2}
          value={dati.sigla_provincia} onChange={e => setDati({ ...dati, sigla_provincia: e.target.value })}
          placeholder="SA" />
      </LabelInput>
      <LabelInput label="Tipo *">
        <select className="input-filtro" style={{ width: '100%' }}
          value={dati.tipo} onChange={e => setDati({ ...dati, tipo: e.target.value })}>
          {TIPI.map(tp => <option key={tp.id} value={tp.id}>{tp.label}</option>)}
        </select>
      </LabelInput>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, margin: 0 }}>Gestione tribunali</h1>
        {!mostraNuovo && (
          <button className="btn-cerca" onClick={() => { setMostraNuovo(true); setAvviso(null); setEditId(null); }}>
            <Plus size={16} /> Nuovo tribunale
          </button>
        )}
      </div>

      {errore && (
        <div style={{ fontSize: 13, color: '#C53030', background: '#FCECEA', padding: '8px 12px',
          borderRadius: 8, marginBottom: 12 }}>{errore}</div>
      )}
      {avviso && (
        <div style={{ fontSize: 13, color: '#276749', background: '#EBF9F1', padding: '8px 12px',
          borderRadius: 8, marginBottom: 12 }}>{avviso}</div>
      )}

      {mostraNuovo && (
        <div style={{ background: '#F7FDFB', border: '1px solid #C6EFE3', borderRadius: 10,
          padding: 16, marginBottom: 16 }}>
          {campiForm(nuovo, setNuovo, true)}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-cerca" onClick={crea} disabled={salvando}>
              {salvando ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />} Crea tribunale
            </button>
            <button onClick={() => { setMostraNuovo(false); setNuovo(VUOTO); setErrore(null); }}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #dde1e7',
                background: 'white', cursor: 'pointer', color: '#718096' }}>Annulla</button>
          </div>
        </div>
      )}

      {/* Filtri */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setFiltroTipo('')}
          style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
            border: `1px solid ${!filtroTipo ? '#1A1A2E' : '#dde1e7'}`,
            background: !filtroTipo ? '#1A1A2E' : 'white', color: !filtroTipo ? 'white' : '#4A5568' }}>
          Tutti · {tribunali.filter(t => t.attivo !== false).length}
        </button>
        {TIPI.map(tp => (
          <button key={tp.id} onClick={() => setFiltroTipo(filtroTipo === tp.id ? '' : tp.id)}
            style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
              border: `1px solid ${filtroTipo === tp.id ? tp.colore : '#dde1e7'}`,
              background: filtroTipo === tp.id ? tp.colore : 'white',
              color: filtroTipo === tp.id ? 'white' : '#4A5568' }}>
            {tp.label} · {conteggi[tp.id]}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 12, color: '#718096', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={mostraInattivi} onChange={e => setMostraInattivi(e.target.checked)} />
          mostra disattivati
        </label>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 8, top: 9, color: '#a0aec0' }} />
          <input className="input-filtro" style={{ paddingLeft: 28, width: 220 }}
            value={ricerca} onChange={e => setRicerca(e.target.value)} placeholder="Cerca nome, città, sigla…" />
        </div>
      </div>

      {caricamento ? (
        <div style={{ color: '#a0aec0', padding: 24 }}><Loader size={18} className="spin" /> Caricamento…</div>
      ) : (
        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #edf0f4', overflow: 'hidden' }}>
          {visibili.map((t, i) => {
            const tp = tipoInfo(t.tipo);
            return (
              <div key={t.id} style={{ padding: '10px 16px', borderTop: i ? '1px solid #f2f4f8' : 'none',
                opacity: t.attivo === false ? 0.5 : 1 }}>
                {editId === t.id ? (
                  <div>
                    {campiForm(edit, setEdit, false)}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-cerca" onClick={() => salvaModifica(t.id)} disabled={salvando}>
                        <CheckCircle size={14} /> Salva
                      </button>
                      <button onClick={() => setEditId(null)} style={{ background: 'none', border: '1px solid #dde1e7',
                        borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#718096' }}><X size={14} /> Annulla</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: `${tp.colore}18`, color: tp.colore, whiteSpace: 'nowrap', minWidth: 120, textAlign: 'center' }}>
                      {tp.label}
                    </span>
                    <div style={{ flex: 2, fontWeight: 600, color: '#1A1A2E' }}>{t.nome}</div>
                    <div style={{ flex: 1, fontSize: 13, color: '#718096' }}>
                      {t.citta}{t.sigla_provincia ? ` (${t.sigla_provincia})` : ''}
                    </div>
                    <div style={{ width: 90, fontSize: 12, color: '#a0aec0', textAlign: 'right' }}
                      title="Ricorsi che usano questo tribunale">
                      {t.n_ricorsi ? `${t.n_ricorsi} ricorsi` : '—'}
                    </div>
                    <button onClick={() => { setEditId(t.id); setMostraNuovo(false);
                        setEdit({ nome: t.nome, citta: t.citta || '', sigla_provincia: t.sigla_provincia || '', tipo: t.tipo });
                        setErrore(null); setAvviso(null); }}
                      style={{ background: 'none', border: '1px solid #dde1e7', borderRadius: 6,
                        padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#4A5568',
                        display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Pencil size={12} /> Modifica
                    </button>
                    <button onClick={() => toggleAttivo(t)}
                      style={{ background: t.attivo === false ? '#EBF9F1' : '#FFF7ED',
                        border: `1px solid ${t.attivo === false ? '#276749' : '#C05621'}`,
                        color: t.attivo === false ? '#276749' : '#C05621',
                        borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                      {t.attivo === false ? 'Riattiva' : 'Disattiva'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {!visibili.length && (
            <div style={{ padding: 24, color: '#a0aec0', fontSize: 13 }}>Nessun tribunale corrisponde ai filtri.</div>
          )}
        </div>
      )}
      <div style={{ fontSize: 12, color: '#a0aec0', marginTop: 10 }}>
        I tribunali disattivati restano sui ricorsi esistenti ma non compaiono più nell'autocompletamento dei form.
      </div>
    </div>
  );
}
